var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useCallback, useRef } from 'react';
import { CopilotContext } from './context';
import { createCompositeKey } from './constants';
/**
 * Copilot Context Provider Component
 *
 * This provider maintains a map of domains to their Copilot context data.
 * It provides functions to get and set data for specific domains and keys.
 */
var CopilotProvider = function (_a) {
    var children = _a.children;
    // State to store domain-specific Copilot data
    var _b = __read(useState({}), 2), domainMap = _b[0], setDomainMap = _b[1];
    // Reference to store values by composite key
    var refsMap = useRef({});
    // Get full data for a specific domain and feature
    var getDomainData = useCallback(function (scope) {
        var key = createCompositeKey(scope.domain, scope.feature);
        return domainMap[key];
    }, [domainMap]);
    // Set full data for a specific domain and feature
    var setDomainData = useCallback(function (scope, data) {
        var key = createCompositeKey(scope.domain, scope.feature);
        setDomainMap(function (prevMap) {
            var _a;
            return (__assign(__assign({}, prevMap), (_a = {}, _a[key] = data, _a)));
        });
    }, []);
    // Get a specific key from a domain and feature's data
    var getKey = useCallback(function (scope, key) {
        var compositeKey = createCompositeKey(scope.domain, scope.feature);
        var domainData = domainMap[compositeKey];
        if (!domainData)
            return undefined;
        return domainData[key];
    }, [domainMap]);
    // Set a specific key for a domain and feature
    var setKey = useCallback(function (scope, key, value) {
        var compositeKey = createCompositeKey(scope.domain, scope.feature);
        setDomainMap(function (prevMap) {
            var _a, _b;
            // If domain+feature doesn't exist yet, create it with default empty values
            var currentDomainData = prevMap[compositeKey] || {
                sessionId: '',
                sessionMode: '',
                conversation: { history: [] },
                query: '',
                autoExecute: false,
                ref: null,
                locationId: '',
                channelType: '',
                openCopilot: false,
            };
            return __assign(__assign({}, prevMap), (_a = {}, _a[compositeKey] = __assign(__assign({}, currentDomainData), (_b = {}, _b[key] = value, _b)), _a));
        });
    }, []);
    // Set the entire domain map
    var setEntireDomainMap = useCallback(function (map) {
        setDomainMap(map);
    }, []);
    // Get ref for a specific domain and feature
    var getRef = useCallback(function (scope) {
        var compositeKey = createCompositeKey(scope.domain, scope.feature);
        return refsMap.current[compositeKey];
    }, []);
    // Set ref for a specific domain and feature
    var setRef = useCallback(function (scope, value) {
        var compositeKey = createCompositeKey(scope.domain, scope.feature);
        refsMap.current[compositeKey] = value;
    }, []);
    var contextValue = {
        domainMap: domainMap,
        getDomainData: getDomainData,
        setDomainData: setDomainData,
        getKey: getKey,
        setKey: setKey,
        getRef: getRef,
        setRef: setRef,
        setDomainMap: setEntireDomainMap,
    };
    return _jsx(CopilotContext.Provider, __assign({ value: contextValue }, { children: children }), void 0);
};
export default CopilotProvider;
//# sourceMappingURL=Provider.js.map